import { FC, useEffect, useMemo, useState } from "react";
import { Input } from "antd";
import { debounce } from "lodash";
import GridWrapper from "../../../components/controls/GridWrapper";
import { SearchIcon } from "../../../libs/corporate/icons/outlined/action/MainCollection";
import { LoadingStatus } from "../../../@types/enumsGlobal";
import { DefaultGrid } from "../../../components/grids/default/defaultGrid";
import { PluginWrapper, ToolbarWrapper } from "../../../components/plugins";
import { ICompanyViewDTO, ICreateCompany, IEditCompany } from "../../../libs/coreapi-dto/service/company";
import { CompanyDataProvider } from "../../../Services/DataProviders/CompanyDataProvider";
import useGridFilter from "../../../system/hooks/useGridFilter";
import { useAppContext } from "../../../system/providers/appContextProvider";
import { CompaniesCommandPanel } from "./Components/CompaniesPanel";
import { CreateCompaniesModal, TypeOpen } from "./Components/CreateCompaniesModal";
import styles from '../../styles/index.module.scss'
import { DatabaseDataProvider } from "../../../Services/DataProviders/DatabaseDataProvider";
import { MessageModalWindow } from "../../../components/modalWindows/MessageModalWindow";
import { Spinner } from "../../../components/spiner/Spinner";
import { checkLockStatus } from "../../CommonHelperFunctions";
import { useUserContext } from "../../../system/providers/userContextProvider";
import { ICompanyLicenseValidateResultDTO } from "../../../libs/coreapi-dto/service/license";
import renderGlobalAlert from "../../../system/hooks/useGlobalAlert";
import { useTranslation } from "react-i18next";

const MIN_SEARCH_LENGTH = 3;

interface IMessageModalProps {
    show: boolean,
    message: string,
    handler?: () => void
}

const CompaniesGrid: FC<IGridProps> = (props) => {
    const appCtx = useAppContext();
    const companyDataProvider = new CompanyDataProvider(appCtx.coreApiService);
    const databaseDataProvider = new DatabaseDataProvider(appCtx.coreApiService);

    const [data, setData] = useState<ICompanyViewDTO[]>([]);
    const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>(LoadingStatus.InProcess);
    const [totalCount, setTotalCount] = useState(0);
    const [viewState, setViewState] = useState<GridStateType>("view");
    const [gridFilter, dispatchGridFilter] = useGridFilter();
    const [selectedItem, setSelectedItem] = useState<IGridRow>();
    const [selectedItems, setSelectedItems] = useState<IGridRow[]>([]);
    const [openCreateModal, setOpenCreateModal] = useState<boolean>(false);
    const [typeOpen, setTypeOpen] = useState<TypeOpen>('Create')
    const [selectedCompany, setSelectedCompany] = useState<ICompanyViewDTO | null>(null);

    const [searchValue, setSearchValue] = useState<string>('');
    const [showMessageModal, setShowMessageModal] = useState<IMessageModalProps>({ show: false, message: '' });
    const [migrateProcess, setMigrateProcess] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
    const [companyToSetCentralOffice, setCompanyToSetCentralOffice] = useState<IGridRow | null>(null);

    const userContext = useUserContext();
    const lockFromPermission = checkLockStatus(props.plugin.permission as IPermission, userContext.userPermission);

    const { t } = useTranslation();
    const baseT = (value: string) => t('directory.companies.' + value)

    const searchCompanies = (value: string) => {
        if (value.length >= MIN_SEARCH_LENGTH) {
            const expression = `lower("Name") like lower('%${value}%')`;
            dispatchGridFilter({ type: "addCustomWhereExpressionFilter", payload: expression });
        } else if (value.length === 0) {
            dispatchGridFilter({ type: "deleteCustomWhereExpressionFilter" });
        }
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchValue(value);
        if (value.length >= MIN_SEARCH_LENGTH || value.length === 0) {
            debouncedSearch(value);
        }
    };

    const handlePressEnter = () => {
        debouncedSearch.cancel();
        if (searchValue.length >= MIN_SEARCH_LENGTH || searchValue.length === 0) {
            searchCompanies(searchValue);
        }
    };

    const debouncedSearch = useMemo(
        () => debounce(searchCompanies, 1250),
        []
    );

    useEffect(() => {
        return () => debouncedSearch.cancel();
    }, []);

    const refresh = () => {
        setViewState('refresh')
        setSelectedItem(undefined)
        setSelectedCompany(null)
        setCompanyToSetCentralOffice(null)
        setShowConfirmModal(false)
    }

    const blockCompany = () => {
        if (selectedItem) {
            companyDataProvider.blockCompany(selectedItem.idGlobal, () => {
                refresh()
            })
        }
    }

    const migrationCompany = () => {
        if (selectedItem) {
            setMigrateProcess(true);
            databaseDataProvider.migrateCompany(selectedItem.idGlobal, (e) => {
                setMigrateProcess(false);
                if (e === true)
                {
                    setShowMessageModal({ show: true, message: baseT("successMigration") })
                    companyDataProvider.isMigrated(selectedItem.idGlobal, ()=>{
                        refresh();
                    })
                }
            })
        }
    }

    const openCentralOfficeConfirmation = () => {
        if (selectedItem && selectedCompany && selectedCompany.isCentralOffice === false) {
            setCompanyToSetCentralOffice(selectedItem);
            setShowConfirmModal(true);
        }
    }

    const confirmSetCentralOffice = () => {
        if (companyToSetCentralOffice) {
            companyDataProvider.setCentralOffice(
                companyToSetCentralOffice.idGlobal,
                () => {
                    renderGlobalAlert({
                        variant: "info",
                        statusCode: 200,
                        title: baseT("successCentralOffice")
            });
                    refresh();
                },
            );
            setShowConfirmModal(false);
            setCompanyToSetCentralOffice(null);
        }
    }

    const cancelSetCentralOffice = () => {
        setShowConfirmModal(false);
        setCompanyToSetCentralOffice(null);
    }

    const unBlockCompany = () => {
        if (selectedItem) {
            companyDataProvider.unBlockCompany(selectedItem.idGlobal, () => {
                refresh()
            })
        }
    }

    const loadData = () => {
        setLoadingStatus(LoadingStatus.InProcess)
        companyDataProvider.viewCompany(gridFilter, (entities, totalCount) => {
            try {
                setData([...entities.map(x => ({ 
                    idGlobal: x.id, 
                    name: x.name, 
                    databaseName: x.databaseName,
                    lockoutEnd: x.lockoutEnd, 
                    licenseName: x.licenseName,
                    dateLicense: x.dateLicense,
                    dateCreated: x.dateCreated,
                    dateModified: x.dateModified,
                    licenseFile: x.licenseFile,
                    isCentralOffice: x.isCentralOffice
                }))])
                setTotalCount(totalCount)
                setLoadingStatus(LoadingStatus.Completed)
                setIsSubmitting(true)
                setMigrateProcess(false);
            } catch {
                setLoadingStatus(LoadingStatus.Failed)
                setMigrateProcess(false);
            }
        })
    }
    useEffect(() => {
        if (viewState === 'refresh') {
            setMigrateProcess(true);
            loadData()
            setViewState('view')
        }
    }, [viewState])

    useEffect(() => {
        try {
            loadData()
        } catch {
            setLoadingStatus(LoadingStatus.Failed)
        }
    }, [gridFilter])

    useEffect(() => {
        if (selectedItem?.idGlobal) {
            companyDataProvider.findCompany(selectedItem.idGlobal, (data)  => {
                setSelectedCompany(
                    {
                        idGlobal: data.id,
                        name: data.name,
                        lockoutEnd: data.lockoutEnd,
                        connectionString: data.connectionString,
                        isMigrated: data.isMigrated,
                        licenseName: data.licenseName,
                        licenseFile: data.licenseFile,
                        isCentralOffice: data.isCentralOffice
                    }
                )
            })  
        } else {
            setSelectedCompany(null)
        }
    }, [selectedItem])

    return (
        <PluginWrapper>

            <ToolbarWrapper>
                <CompaniesCommandPanel
                    add={{
                        onClick: () => {
                            setTypeOpen('Create');
                            setOpenCreateModal(true);
                        }
                    }}
                    edit={{
                        onClick: () => {
                            setTypeOpen('Edit');
                            setOpenCreateModal(true);
                        },
                        disabled: selectedItem?.idGlobal ? false : true
                    }}
                    migration={{
                        onClick: () => {
                            migrationCompany()
                        },
                        disabled: selectedCompany && selectedCompany.connectionString && !selectedCompany.isMigrated && !migrateProcess ? false : true
                    }}
                    centralOffice={{
                        onClick: () => {
                            openCentralOfficeConfirmation()
                        },
                        disabled: !selectedItem || selectedCompany?.isCentralOffice === true
                    }}
                    block={{
                        onClick: () => {
                            blockCompany();
                        },
                        disabled: selectedCompany && selectedCompany.lockoutEnd === null ? false : true
                    }}
                    unBlock={{
                        onClick: () => {
                            unBlockCompany()
                        },
                        disabled: selectedCompany && selectedCompany.lockoutEnd !== null ? false : true
                    }}
                    refresh={{
                        onClick: () => {
                            refresh();
                        }
                    }}
                    permission={props.plugin.permission}
                />
            </ToolbarWrapper>
            <GridWrapper cols={5}>
                <Input
                    className="custom-input-withPrefix"
                    value={searchValue}
                    placeholder={baseT("searchCompany")}
                    prefix={<SearchIcon />}
                    onPressEnter={handlePressEnter}
                    onChange={handleSearchChange}
                    allowClear
                />
            </GridWrapper>
            <div className={styles.gridWrapper}>
                <div className={styles.masterGrid}>
                    {
                        migrateProcess || !isSubmitting ?
                        (<Spinner />) :
                        (<DefaultGrid
                            separator
                            gridId={props.gridId}
                            data={data}
                            loadingStatus={loadingStatus}
                            getView={(gridFilter, callback) => {
                                companyDataProvider.viewCompany(gridFilter, (entities, totalCount) => {
                                    const generateEntities = entities.map(x => ({
                                        idGlobal: x.id,
                                        name: x.name,
                                        lockoutEnd: x.lockoutEnd,
                                        licenseName: x.licenseName,
                                        licenseFile: x.licenseFile,
                                        dateCreated: x.dateCreated,
                                        dateModified: x.dateModified,
                                        databaseName: x.databaseName,
                                        dateLicense: x.dateLicense,
                                        isCentralOffice: x.isCentralOffice
                                    }));
                                    setTotalCount(totalCount);
                                    setData(generateEntities)
                                    callback(generateEntities);
                                })
                            }}   
                            setLoadingStatus={setLoadingStatus}
                            totalCount={totalCount}
                            filter={gridFilter}
                            plugin={props.plugin}
                            selectedItem={selectedItem}
                            dataProvider={companyDataProvider}
                            selectedItems={selectedItems}
                            onMultipleSelect={(rows) => {
                                setSelectedItems(rows)
                                props?.onMultipleSelect?.(rows)
                            }}
                            multipleSelect={props.multipleSelect}
                            onSelect={(row) => {
                                setSelectedItem(row)
                                props?.onSelect?.(row)
                            }}
                            // useUiService={false}
                            onDoubleClick={(i) => props.onDoubleClick?.(i)}
                            onSort={(i) => dispatchGridFilter({ type: "sort", payload: i.propertyName })}
                            onFilterDelete={(i) => dispatchGridFilter({ type: "deleteColumnFilter", payload: i.propertyName })}
                            onPageNumberChange={(n) => dispatchGridFilter({ type: "changePageNumber", payload: { pageNumber: n } })}
                            onNumberPerPageChange={(n) => dispatchGridFilter({ type: "changeNumberPerPage", payload: { numberPerPage: n } })}
                            onEnter={() => setViewState("edit")}
                            renderCustomCell={(cell: IGridCell) => {
                                if (cell.propertyName === "isCentralOffice") {
                                    return (
                                        <input type={'checkbox'} checked={cell.value}/>
                                    )
                                }
                                return null;
                            }} 
                        />)
                    }
                </div></div>

            {
                showMessageModal.show &&
                <MessageModalWindow
                    ok={{ onClick: () => { 
                        if (showMessageModal.handler) { 
                            showMessageModal.handler() 
                        }
                        setShowMessageModal({ show: false, message: '' }) 
                    }}}
                    cancel={{ hidden: true,
                        onClick: () => { setShowMessageModal({ show: false, message: '' }) } }}
                    message={showMessageModal.message}
                />
            }
            {showConfirmModal && companyToSetCentralOffice && (
                <MessageModalWindow
                    ok={{ 
                        onClick: confirmSetCentralOffice 
                    }}
                    cancel={{ 
                        hidden: false,
                        onClick: cancelSetCentralOffice 
                    }}
                    message={t('directory.companies.confirmSetCentralOffice', { companyName: selectedCompany?.name })}
                />
            )}

            {
                openCreateModal &&
                <CreateCompaniesModal
                    ok={(companyData: ICreateCompany | IEditCompany, validate: (() => Promise<ICompanyLicenseValidateResultDTO>) | (() => void), processCompany: ((id: string, callback: () => void) => void) | (() => void)) => {
                        if (typeOpen === 'Create') {

                            // Валидация файла ведомости перед созданием компании
                            (validate() as Promise<ICompanyLicenseValidateResultDTO>).then((result) => {
                                if (result && result.success) {
                                    companyDataProvider.create(companyData as ICreateCompany, (createdCompany) => {

                                        // Если компания создана успешно, подтянуть лицензию в систему
                                        if (createdCompany && "id" in createdCompany) {
                                            processCompany(createdCompany.id, () => {
                                                refresh();
                                                setOpenCreateModal(false);
                                            });
                                        }
                                    }, () => {
                                        renderGlobalAlert({ variant: "danger", statusCode: 400, title: baseT("companyCreateError") });
                                    })
                                }
                            });
                        } else {
                            (validate() as Promise<ICompanyLicenseValidateResultDTO>).then((result) => {
                                if (result && result.success) {
                                    companyDataProvider.update(selectedItem?.idGlobal as string, companyData as IEditCompany, () => {

                                        processCompany(selectedItem?.idGlobal as string, () => {
                                            refresh();
                                            setOpenCreateModal(false);
                                        });
                                    })
                                }
                            });
                        }
                    }}
                    cancel={() => setOpenCreateModal(false)}
                    companyEditData={selectedCompany ? { 
                        companyName: selectedCompany.name, 
                        connectionString: selectedCompany.connectionString ?? '',
                        licenseFile: selectedCompany.licenseFile,
                    } : null}
                    typeOpen={typeOpen}
                    lockFromPermission={lockFromPermission}
                />
            }
        </PluginWrapper>
    )
}

export default CompaniesGrid
